import { useState } from "react";
import { motion } from "framer-motion";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";

// World map topology (CC-BY public source via jsdelivr)
const GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json";

// Country names that we operate in (matches topojson properties.name)
const COVERED = new Set([
  // Asia-Pacific
  "India",
  "Singapore",
  "Malaysia",
  "Thailand",
  "Indonesia",
  "Vietnam",
  "Japan",
  "Australia",
  // Middle East
  "United Arab Emirates",
  "Saudi Arabia",
  "Qatar",
  "Kuwait",
  "Bahrain",
  "Oman",
  // North America
  "United States of America",
  "Canada",
  // Europe
  "United Kingdom",
  "Germany",
  "France",
  "Netherlands",
  "Spain",
]);

// Hub markers with labels [lng, lat]
const HUBS = [
  { name: "UAE", coords: [55, 24], region: "Middle East" },
  { name: "India", coords: [78, 21], region: "Asia-Pacific" },
  { name: "Malaysia", coords: [102, 4], region: "Asia-Pacific" },
];

export default function WorldCoverageMap() {
  const [hovered, setHovered] = useState(null);

  return (
    <div
      data-testid="world-coverage-map"
      className="relative w-full"
    >
      <div className="relative aspect-[16/9] w-full">
        <ComposableMap
          projection="geoEqualEarth"
          projectionConfig={{ scale: 175 }}
          width={980}
          height={520}
          style={{ width: "100%", height: "100%" }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const name = geo.properties.name;
                const isCovered = COVERED.has(name);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => isCovered && setHovered(name)}
                    onMouseLeave={() => setHovered(null)}
                    style={{
                      default: {
                        fill: isCovered ? "#0d9488" : "#142a48",
                        stroke: isCovered
                          ? "rgba(45,212,191,0.8)"
                          : "rgba(255,255,255,0.06)",
                        strokeWidth: isCovered ? 0.6 : 0.4,
                        outline: "none",
                      },
                      hover: {
                        fill: isCovered ? "#14b8a6" : "#142a48",
                        stroke: isCovered ? "#2dd4bf" : "rgba(255,255,255,0.06)",
                        strokeWidth: isCovered ? 0.8 : 0.4,
                        outline: "none",
                        cursor: isCovered ? "pointer" : "default",
                      },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {HUBS.map((hub) => (
            <Marker key={hub.name} coordinates={hub.coords}>
              <g>
                <circle r={8} fill="rgba(234,88,12,0.18)">
                  <animate
                    attributeName="r"
                    from="5"
                    to="13"
                    dur="2.4s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    from="0.6"
                    to="0"
                    dur="2.4s"
                    repeatCount="indefinite"
                  />
                </circle>
                <circle r={3.2} fill="#fb923c" stroke="#fff" strokeWidth={0.8} />
                <text
                  textAnchor="middle"
                  y={-10}
                  style={{
                    fontFamily: "Outfit, sans-serif",
                    fontSize: 10,
                    fontWeight: 600,
                    fill: "#ffffff",
                    letterSpacing: "0.05em",
                    pointerEvents: "none",
                  }}
                >
                  {hub.name}
                </text>
              </g>
            </Marker>
          ))}
        </ComposableMap>

        {/* Hover label */}
        {hovered && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-4 left-4 bg-white text-slate-900 text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-200 shadow-md tracking-wide"
            data-testid="world-map-hover-label"
          >
            {hovered}
          </motion.div>
        )}
      </div>

      {/* Legend */}
      <div
        className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs"
        data-testid="world-map-legend"
      >
        <div className="flex items-center gap-2 text-white/80">
          <span className="inline-block h-3 w-3 rounded-sm bg-teal-600 border border-teal-400" />
          Covered country
        </div>
        <div className="flex items-center gap-2 text-white/80">
          <span className="inline-block h-3 w-3 rounded-full bg-orange-500 ring-2 ring-orange-500/30" />
          Operations hub
        </div>
        <div className="flex items-center gap-2 text-white/60">
          <span className="inline-block h-3 w-3 rounded-sm bg-[#142a48] border border-white/10" />
          Available on request
        </div>
      </div>
    </div>
  );
}
